// Channel / DM header bar + the in-chat search dropdown (works for any channel
// type). Structural subsets of ChatLayout's types (ground rule 5: no back-imports).
type SearchFilter = 'all' | 'from' | 'has' | 'mentions';
type SearchMsg = { id: string; authorName?: string; content?: string; createdAt?: string };

type ChannelHeaderProps = {
  isDmSpace: boolean;
  // DM header
  dmPeer?: { avatarUrl?: string | null } | null;
  dmPeerName?: string | null;
  onOpenPeerProfile: () => void;
  voiceCall: { phase: string; canCall: boolean; busy: boolean; livekitConfigured: boolean; onClick: () => void };
  onAddPerson: () => void;
  /** Pin this conversation to the home page. Omit to hide the star. */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  // Regular channel header
  currentChannel?: { name?: string | null } | null;
  currentChannelId: string;
  currentSpaceOnlineCount: number;
  onShowOnline: () => void;
  // Search (shared between DM + regular headers)
  searchRef: React.Ref<HTMLDivElement>;
  searchOpen: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchFilter: SearchFilter;
  searchFromUser: string;
  searchLoading: boolean;
  searchResults: SearchMsg[];
  onToggleSearch: () => void;
  onClearSearch: () => void;
  onClearFilter: () => void;
  onSelectFilter: (filter: 'from' | 'has' | 'mentions') => void;
  onOpenSearchResult: (messageId: string) => void;
};

function ChannelHeader({
  isDmSpace,
  dmPeer,
  dmPeerName,
  onOpenPeerProfile,
  voiceCall,
  onAddPerson,
  isFavorite,
  onToggleFavorite,
  currentChannel,
  currentChannelId,
  currentSpaceOnlineCount,
  onShowOnline,
  searchRef,
  searchOpen,
  searchQuery,
  setSearchQuery,
  searchFilter,
  searchFromUser,
  searchLoading,
  searchResults,
  onToggleSearch,
  onClearSearch,
  onClearFilter,
  onSelectFilter,
  onOpenSearchResult,
}: ChannelHeaderProps) {
  return (
    <div className="fixed top-[calc(env(safe-area-inset-top)+64px)] left-0 right-0 z-20 md:z-10 w-full sm:sticky sm:top-0 sm:left-auto sm:right-auto">
      {isDmSpace ? (
        /* ── DM header ── */
        <div className="flex flex-row items-center gap-2 px-3 bg-panel-0 border-b border-white/5" style={{ height: 48 }}>
          {/* Peer avatar → profile */}
          <button
            className="relative shrink-0 h-8 w-8 rounded-full overflow-hidden bg-white/10 flex items-center justify-center hover:ring-2 hover:ring-white/30 transition-shadow"
            title={dmPeerName || 'View profile'}
            onClick={onOpenPeerProfile}
          >
            {dmPeer?.avatarUrl
              ? <ThumbImg w={56} src={dmPeer.avatarUrl} alt={dmPeerName || ''} className="h-full w-full object-cover" />
              : <span className="text-xs font-medium text-fg-2">{(dmPeerName || '?')[0].toUpperCase()}</span>
            }
          </button>
          {/* Peer name */}
          <h1 className="flex-1 min-w-0"><FitText className="font-medium text-white" maxRem={0.875} minRem={0.7}>{dmPeerName || 'Direct Message'}</FitText></h1>
          {/* Action icons */}
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Voice call */}
            <button
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${voiceCall.phase === 'in_call' ? 'text-red-400 bg-red-500/15 hover:bg-red-500/25' : voiceCall.phase === 'outgoing' ? 'text-amber-400 bg-amber-500/15' : 'text-fg-2 hover:text-white hover:bg-white/8'} disabled:opacity-40`}
              title={!voiceCall.livekitConfigured && voiceCall.phase === 'idle' ? 'Voice calls are not configured on this server' : voiceCall.phase === 'in_call' ? 'End call' : voiceCall.phase === 'outgoing' ? 'Cancel call' : 'Start voice call'}
              disabled={(!voiceCall.canCall && voiceCall.phase === 'idle') || voiceCall.busy}
              onClick={voiceCall.onClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.88a16 16 0 0 0 6.16 6.16l1.76-1.76a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z"/></svg>
            </button>
            {/* Video call — stub, not yet implemented */}
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-3 cursor-not-allowed"
              title="Video call (coming soon)"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            </button>
            {/* Pinned messages — stub */}
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-3 cursor-not-allowed"
              title="Pinned messages (coming soon)"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17z"/></svg>
            </button>
            {/* Favorite: pins this conversation to the home page */}
            {onToggleFavorite && (
              <button
                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${isFavorite ? 'text-amber-300 hover:bg-white/8' : 'text-fg-2 hover:text-white hover:bg-white/8'}`}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={!!isFavorite}
                onClick={onToggleFavorite}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </button>
            )}
            {/* Add person */}
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-2 hover:text-white hover:bg-white/8 transition-colors"
              title="Add person to conversation"
              onClick={onAddPerson}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
            </button>
            {/* Search in chat */}
            <button
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${searchOpen ? 'text-white bg-white/10' : 'text-fg-2 hover:text-white hover:bg-white/8'}`}
              title="Search in conversation"
              onClick={onToggleSearch}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </button>
          </div>
        </div>
      ) : (
        /* ── Regular channel header ── */
        <div className="h-10 md:h-12 flex items-center justify-between gap-3 px-4 bg-transparent backdrop-blur-none border-b border-white/5">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="min-w-0 max-w-[60vw]"><FitText className="font-medium text-fg" maxRem={1} minRem={0.75}>#{currentChannel?.name ?? currentChannelId}</FitText></h1>
            {currentSpaceOnlineCount > 0 && (
              // Desktop shows the online count on the People column / collapsed handle; phones have no such column, so keep it there.
              <button
                type="button"
                className="md:hidden text-xs text-fg-2 whitespace-nowrap underline-offset-2 hover:underline focus:outline-none focus:ring-1 focus:ring-emerald-400 rounded"
                onClick={onShowOnline}
                aria-label={`Show ${currentSpaceOnlineCount} online members`}
              >
                {currentSpaceOnlineCount} online
              </button>
            )}
          </div>
          {/* Search — available on all text channels */}
          <button
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${searchOpen ? 'text-white bg-white/10' : 'text-fg-2 hover:text-white hover:bg-white/8'}`}
            title="Search in channel"
            onClick={onToggleSearch}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          </button>
        </div>
      )}
      {/* In-chat search dropdown — works for any channel type */}
      {searchOpen && (
        <div ref={searchRef} className="bg-neutral-900 px-3 py-3 space-y-2 shadow-xl">
          {/* Query input */}
          <div className="flex items-center gap-2 rounded-xl bg-neutral-800 border border-white/10 px-3 py-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-fg-3 shrink-0"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              autoFocus
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchFilter === 'from' ? `Search for ${searchFromUser || 'user'}…` : searchFilter === 'has' ? 'Search links, files, embeds…' : searchFilter === 'mentions' ? 'Search mentions…' : `Search in ${isDmSpace ? (dmPeerName || 'conversation') : `#${currentChannel?.name || currentChannelId}`}…`}
              aria-label={searchFilter === 'from' ? `Search for ${searchFromUser || 'user'}…` : searchFilter === 'has' ? 'Search links, files, embeds…' : searchFilter === 'mentions' ? 'Search mentions…' : `Search in ${isDmSpace ? (dmPeerName || 'conversation') : `#${currentChannel?.name || currentChannelId}`}…`}
              className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-fg-3 min-w-0"
            />
            {(searchQuery || searchFilter !== 'all') && (
              <button className="text-fg-3 hover:text-white shrink-0" onClick={onClearSearch}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          {/* Active filter chip */}
          {searchFilter !== 'all' && (
            <div className="flex items-center gap-1.5">
              <span className="flex items-center gap-1 rounded-full bg-white/10 border border-white/15 px-2 py-0.5 text-xs text-fg-2">
                {searchFilter === 'from' && <><span className="text-fg-3">from:</span>{searchFromUser || 'user'}</>}
                {searchFilter === 'has' && <><span className="text-fg-3">has:</span>link, embed or file</>}
                {searchFilter === 'mentions' && <><span className="text-fg-3">mentions:</span>user</>}
                <button className="ml-0.5 text-fg-3 hover:text-white" onClick={onClearFilter}>×</button>
              </span>
            </div>
          )}
          {/* Filter options — shown when no query yet */}
          {!searchQuery && searchFilter === 'all' && (
            <div className="space-y-0.5">
              <p className="px-1 text-meta text-fg-3 font-medium pb-1">Filters</p>
              {[
                { id: 'from' as const, icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>, label: 'From a specific user', hint: 'from: user' },
                { id: 'has' as const, icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>, label: 'Includes a specific type of data', hint: 'has: link, embed or file' },
                { id: 'mentions' as const, icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg>, label: 'Mentions a specific user', hint: 'mentions: user' },
              ].map(f => (
                <button
                  key={f.id}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/6 text-left transition-colors"
                  onClick={() => { onSelectFilter(f.id); }}
                >
                  <span className="shrink-0 text-fg-3">{f.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-fg">{f.label}</span>
                    <span className="block text-xs text-fg-3">{f.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {/* Search results — only shown once there's something to search */}
          {(searchQuery.trim() || searchFilter === 'has' || searchFilter === 'mentions') && (
            searchLoading
              ? <p className="px-2 py-3 text-sm text-fg-3 text-center">Searching…</p>
              : searchResults.length === 0
                ? <p className="px-2 py-3 text-sm text-fg-3 text-center">No results found</p>
                : (
                  <div className="space-y-0.5 max-h-64 overflow-y-auto">
                    {searchResults.map(m => (
                      <button
                        key={m.id}
                        className="w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left transition-colors hover:bg-white/8"
                        onClick={() => onOpenSearchResult(m.id)}
                      >
                        <span className="shrink-0 text-meta text-fg-3 mt-0.5 w-16 truncate">{m.authorName || 'Unknown'}</span>
                        <p className="flex-1 text-xs text-fg-2 line-clamp-2 min-w-0">{m.content}</p>
                        {m.createdAt && <span className="shrink-0 text-meta text-fg-3">{new Date(m.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                      </button>
                    ))}
                  </div>
                )
          )}
        </div>
      )}
    </div>
  );
}

export default ChannelHeader;
import ThumbImg from "../media/ThumbImg";
import FitText from '../ui/FitText';
